import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// API base can be configured via .env. In local dev, a relative "/solvit-api" would hit
// the React dev server (localhost:3000) and return index.html (HTML) instead of JSON.
// So: if we are on localhost and API_BASE is relative, we automatically target the server.
const RAW_API_BASE = process.env.REACT_APP_SOLVIT_API_BASE || "/solvit-api";
const RAW_API_HOST = process.env.REACT_APP_SOLVIT_API_HOST || "82.165.15.24";

// Prevent common .env mistakes (quotes/whitespace/missing scheme) that can crash fetch()
function normalizeApiBase(input) {
  const trimmed = String(input || "").trim().replace(/^["']|["']$/g, "");
  if (!trimmed) return "/solvit-api";

  // Absolute URLs stay as-is
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;

  // Relative path (e.g. /solvit-api)
  if (trimmed.startsWith("/")) {
    // When developing locally, route API calls to the real server (nginx) instead of localhost:3000
    if (typeof window !== "undefined" && window.location && window.location.hostname === "localhost") {
      return `http://${RAW_API_HOST}${trimmed}`;
    }
    // When running on the server (or any non-localhost host), relative is fine
    return trimmed;
  }

  // If someone configured `82.165.15.24/solvit-api` without scheme, assume http://
  return `http://${trimmed}`;
}

const API_BASE = normalizeApiBase(RAW_API_BASE);

function StatCard({ title, value, subtitle, icon, cardColor }) {
  return (
    <Card
      sx={{
        borderRadius: "0.7rem",
        border: "1px solid #d4deea",
        boxShadow: "none",
        overflow: "hidden",
      }}
    >
      <MDBox
        sx={{
          display: "grid",
          gridTemplateColumns: "74px 1fr",
          alignItems: "stretch",
          background: cardColor || "linear-gradient(135deg, #0d8df6 0%, #077be2 100%)",
        }}
      >
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ borderRight: "1px solid rgba(255,255,255,0.35)" }}
        >
          <Icon sx={{ color: "white", fontSize: "1.55rem !important" }}>{icon}</Icon>
        </MDBox>
        <MDBox px={2} py={1.2}>
          <MDTypography variant="h4" fontWeight="bold" color="white" sx={{ lineHeight: 1.1 }}>
            {value}
          </MDTypography>
          <MDTypography variant="button" color="white" sx={{ opacity: 0.92 }}>
            {title}
          </MDTypography>
        </MDBox>
      </MDBox>
      {subtitle && (
        <MDBox px={2} py={1.1} bgcolor="white">
          <MDTypography variant="caption" color="text" display="block">
            {subtitle}
          </MDTypography>
        </MDBox>
      )}
    </Card>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("solvit_auth_token");
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [customers, setCustomers] = useState([]);
  const [screens, setScreens] = useState([]);
  const [media, setMedia] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [screensWithoutLoopCount, setScreensWithoutLoopCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;

    const fetchJson = async (url, init) => {
      let res;

      // Hard timeout so the dashboard can't "load forever"
      const controller = new AbortController();
      const timeoutMs = 8000;
      const t = setTimeout(() => controller.abort(), timeoutMs);

      const mergedInit = {
        ...(init || {}),
        signal: controller.signal,
      };

      // Catch invalid URL / network errors clearly
      try {
        res = await fetch(url, mergedInit);
      } catch (err) {
        const msg =
          err?.name === "AbortError"
            ? `Timeout after ${timeoutMs}ms`
            : err?.message || err;
        throw new Error(`Fetch failed for "${url}": ${msg}`);
      }

      // Non-OK HTTP status → read body for debugging
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        clearTimeout(t);
        const err = new Error(
          `Request failed (${res.status}) for "${url}": ${txt || "no response body"}`
        );
        // attach status so callers can branch on 401/404 etc.
        err.status = res.status;
        err.body = txt;
        throw err;
      }

      // Read raw text first to detect HTML / proxy errors
      const text = await res.text().catch(() => "");
      clearTimeout(t);
      try {
        return JSON.parse(text);
      } catch {
        throw new Error(
          `Invalid JSON from "${url}". First chars: ${text
            .slice(0, 120)
            .replace(/\\n/g, " ")}`
        );
      }
    };

    const load = async () => {
      setLoading(true);
      setError("");

      // If not logged in, go to login immediately and do not spam requests.
      if (!token) {
        if (!alive) return;
        setLoading(false);
        setError("Bitte zuerst einloggen.");
        window.location.href = "/authentication/sign-in";
        return;
      }

      try {
        // 1) customers
        const cData = await fetchJson(`${API_BASE}/api/admin/customers`, { headers: authHeaders });
        const cList = Array.isArray(cData) ? cData : cData?.customers || [];

        // 2) screens (admin preferred)
        let sData;
        try {
          sData = await fetchJson(`${API_BASE}/api/admin/screens`, { headers: authHeaders });
        } catch (e) {
          if (e?.status === 404) {
            sData = await fetchJson(`${API_BASE}/api/screens`, { headers: authHeaders });
          } else {
            throw e;
          }
        }
        const sList = Array.isArray(sData) ? sData : sData?.screens || [];

        // 3) invoices aggregate by customer
        const invoiceBuckets = await Promise.all(
          cList.map(async (c) => {
            const cid = c.id;
            if (!cid) return [];
            try {
              const inv = await fetchJson(`${API_BASE}/api/admin/invoices?customerId=${cid}`, {
                headers: authHeaders,
              });
              return Array.isArray(inv) ? inv : inv?.invoices || [];
            } catch (e) {
              if (e?.status === 404) {
                const inv = await fetchJson(`${API_BASE}/api/invoices?customerId=${cid}`, {
                  headers: authHeaders,
                });
                return Array.isArray(inv) ? inv : inv?.invoices || [];
              }
              throw e;
            }
          })
        );
        const invList = invoiceBuckets.flat();

        // 4) media aggregate by customer
        const mediaBuckets = await Promise.all(
          cList.map(async (c) => {
            const cid = c.id;
            if (!cid) return [];
            try {
              const m = await fetchJson(`${API_BASE}/api/admin/media?customerId=${cid}`, {
                headers: authHeaders,
              });
              return Array.isArray(m) ? m : m?.media || m?.items || [];
            } catch (e) {
              if (e?.status === 404) {
                const m = await fetchJson(`${API_BASE}/api/media?customerId=${cid}`, {
                  headers: authHeaders,
                });
                return Array.isArray(m) ? m : m?.media || m?.items || [];
              }
              throw e;
            }
          })
        );
        const mList = mediaBuckets.flat();

        // 5) loops: count screens with empty loop
        const loopCounts = await Promise.all(
          sList.map(async (s) => {
            const sid = s.id;
            if (!sid) return 0;
            try {
              const loop = await fetchJson(`${API_BASE}/api/admin/screens/${sid}/loop`, {
                headers: authHeaders,
              });
              const items = Array.isArray(loop) ? loop : loop?.items || loop?.loopItems || [];
              return items.length;
            } catch (e) {
              if (e?.status === 404) {
                const loop = await fetchJson(`${API_BASE}/api/screens/${sid}/loop`, {
                  headers: authHeaders,
                });
                const items = Array.isArray(loop) ? loop : loop?.items || loop?.loopItems || [];
                return items.length;
              }
              // if loop endpoint missing, treat as unknown (not empty)
              return 1;
            }
          })
        );
        const emptyLoops = loopCounts.filter((n) => n === 0).length;

        if (!alive) return;
        setCustomers(cList);
        setScreens(sList);
        setInvoices(invList);
        setMedia(mList);
        setScreensWithoutLoopCount(emptyLoops);
      } catch (e) {
        if (!alive) return;

        // If token is missing/expired → back to login
        if (e?.status === 401) {
          setLoading(false);
          setError("Session abgelaufen. Bitte erneut einloggen.");
          window.location.href = "/authentication/sign-in";
          return;
        }

        setError(e?.message || "Fehler beim Laden der Dashboard-Daten.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [token, authHeaders]);

  const derived = useMemo(() => {
    const offlineScreens = screens.filter((s) => s?.status === "offline").length;
    const invoicesMissingPdf = invoices.filter((inv) => !inv?.pdfUrl).length;

    const latestInvoice =
      invoices
        .slice()
        .sort((a, b) => {
          const da = new Date(a?.createdAt || a?.issuedAt || 0).getTime();
          const db = new Date(b?.createdAt || b?.issuedAt || 0).getTime();
          return db - da;
        })[0] || null;

    return {
      offlineScreens,
      screensWithoutLoop: screensWithoutLoopCount,
      invoicesMissingPdf,
      latestInvoice,
    };
  }, [screens, invoices, screensWithoutLoopCount]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        {/* Header */}
        <MDBox mb={2}>
          <MDTypography variant="h4" fontWeight="medium" mb={1}>
            Hey, Roman!
          </MDTypography>
          <MDTypography variant="button" color="text">
            Dieser Bereich ist nur für Administratoren. Änderungen wirken sich direkt auf Kunden & Screens aus.
          </MDTypography>
        </MDBox>

        {(loading || error) && (
          <MDBox mb={2}>
            {loading && (
              <MDTypography variant="button" color="text">
                Lädt Dashboard-Daten…
              </MDTypography>
            )}
            {error && (
              <MDTypography variant="button" color="error">
                {error}
              </MDTypography>
            )}
          </MDBox>
        )}

        {/* Stats */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Kunden"
              value={customers.length}
              subtitle="aktive Datensätze"
              icon="groups"
              cardColor="linear-gradient(135deg, #0d8df6 0%, #077be2 100%)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Bildschirme"
              value={screens.length}
              subtitle={`${derived.offlineScreens} offline`}
              icon="tv"
              cardColor="linear-gradient(135deg, #11a1f7 0%, #0b89d8 100%)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Medien"
              value={media.length}
              subtitle="Bilder & Videos"
              icon="perm_media"
              cardColor="linear-gradient(135deg, #ff6a7b 0%, #ef5b56 100%)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Rechnungen"
              value={invoices.length}
              subtitle={
                derived.latestInvoice?.month
                  ? `Letzte: ${derived.latestInvoice.month}`
                  : `${derived.invoicesMissingPdf} ohne PDF`
              }
              icon="receipt_long"
              cardColor="linear-gradient(135deg, #ffc96b 0%, #f0b14a 100%)"
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Actions */}
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: "0.7rem", border: "1px solid #d4deea", boxShadow: "none" }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={1}>
                  Aktionen
                </MDTypography>
                <MDTypography variant="button" color="text" mb={2}>
                  Schnelleinstiege für die wichtigsten Admin-Aufgaben.
                </MDTypography>
                <Divider />
                <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
                  <MDButton variant="gradient" color="info" startIcon={<Icon>groups</Icon>} disabled>
                    Kunden verwalten
                  </MDButton>
                  <MDButton variant="gradient" color="info" startIcon={<Icon>settings_remote</Icon>} onClick={() => navigate("/admin/control-center")}>
                    Control Center öffnen
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    startIcon={<Icon>manage_accounts</Icon>}
                    onClick={() => navigate("/admin/profile")}
                  >
                    Profil verwalten
                  </MDButton>
                  <MDButton variant="gradient" color="info" startIcon={<Icon>upload</Icon>} disabled>
                    Rechnung hochladen
                  </MDButton>
                  <MDButton variant="outlined" color="info" startIcon={<Icon>notifications</Icon>} disabled>
                    Benachrichtigungen
                  </MDButton>
                </MDBox>
                <MDTypography variant="caption" color="text" display="block" mt={1.5}>
                  Hinweis: Das Control Center ist jetzt aktiv, die übrigen Buttons sind noch Platzhalter.
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>

          {/* Hints */}
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: "0.7rem", border: "1px solid #d4deea", boxShadow: "none" }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={1}>
                  Hinweise
                </MDTypography>
                <MDTypography variant="button" color="text" mb={2}>
                  Dinge, die du als Admin im Blick behalten solltest.
                </MDTypography>
                <Divider />

                <MDBox mt={2}>
                  <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                    <Icon fontSize="small">warning</Icon>
                    <MDTypography variant="button" fontWeight="medium">
                      {derived.screensWithoutLoop} Screen(s) ohne Loop
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" color="text" display="block" mb={2}>
                    Ohne Loop wird auf dem Display ggf. nichts abgespielt.
                  </MDTypography>

                  <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                    <Icon fontSize="small">wifi_off</Icon>
                    <MDTypography variant="button" fontWeight="medium">
                      {derived.offlineScreens} Screen(s) offline
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" color="text" display="block" mb={2}>
                    Prüfe Strom/WLAN oder ob der Stick korrekt läuft.
                  </MDTypography>

                  <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                    <Icon fontSize="small">picture_as_pdf</Icon>
                    <MDTypography variant="button" fontWeight="medium">
                      {derived.invoicesMissingPdf} Rechnung(en) ohne PDF
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" color="text" display="block">
                    Diese erscheinen im Kundenportal nicht als Download.
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}
